import React, {Fragment, useRef, useState} from 'react';
import {ExhibitData} from "types/Exhibit";
import {Dialog, Tab, Transition} from "@headlessui/react";
import {XIcon} from "@heroicons/react/solid";
import {ExhibitType} from "types/ExhibitType";
import {PhotographIcon, ThumbDownIcon, ThumbUpIcon} from '@heroicons/react/outline';
import {AnyMedia, AudioFile, ImageFile} from "types/MediaFile";
import {concatenate} from "services/string";
import ExhibitInformation from "pages/Exhibit/Preview/ExhibitInformation";
import ExhibitNotes from "pages/Exhibit/Preview/ExhibitNotes";
import ExhibitQuizzes from "pages/Exhibit/Preview/ExhibitQuizzes";
import {useOperator} from "context/OperatorContext";

type Props = {
    exhibitId?: number
    exhibit: ExhibitData
    exhibitType: ExhibitType | null
    exhibitMedia: Array<AnyMedia>
    onClose: () => any
}

function ExhibitPreview(props: Props) {
    const {exhibit, exhibitType, exhibitMedia, onClose} = props
    const images = exhibitMedia.filter(file => file.type === 'image') as ImageFile[]
    const audio = exhibitMedia.filter(file => file.type === 'audio') as AudioFile[]

    const {termFor} = useOperator()
    const [open, setOpen] = useState<boolean>(true)
    const closeButtonRef = useRef<HTMLButtonElement>(null);

    const handleClose = () => {
        setOpen(false)
        setTimeout(() => onClose(), 210)
    }

    const tabs = [
        {name: "Info", element: <ExhibitInformation description={exhibit.description} audio={audio} facts={exhibit.facts ?? []} information={exhibit.information ?? []}/>},
        {name: "Notes", element: <ExhibitNotes exhibitId={props.exhibitId}/>},
        {name: "Quizzes", element: <ExhibitQuizzes exhibitId={props.exhibitId}/>},
    ];

    return (
        <Transition.Root show={open} appear={true} as={Fragment}>
            <Dialog as="div" className="fixed z-10 inset-0 overflow-y-auto" initialFocus={closeButtonRef}
                    onClose={handleClose}>
                <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center block p-0">

                    {/* Modal Overlay */}
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"/>
                    </Transition.Child>

                    {/* This element is to trick the browser into centering the modal contents. */}
                    <span className="hidden inline-block align-middle h-screen" aria-hidden="true">&#8203;</span>

                    {/* Modal Content */}
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enterTo="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <div
                            className="relative inline-block align-middle bg-gray-200 rounded-lg text-left overflow-hidden shadow-xl transform transition-all w-full md:max-w-xl">
                            <button
                              type="button"
                              className="absolute top-4 right-4 lg:top-6 lg:right-6 z-20"
                              ref={closeButtonRef}
                              onClick={() => handleClose()}
                            >
                                <XIcon className="h-6 w-6 text-white drop-shadow-sm"/>
                            </button>

                            {/* Exhibit name and lead media */}
                            <Tab.Group>
                                {/* Media */}
                                <div className="h-80 md:h-96">
                                    {images.length > 0 ?
                                        <div className="relative w-full h-full flex justify-center">
                                            <img
                                                className="h-full object-cover"
                                                src={images[0].preview_url || images[0].original_url}
                                                alt={exhibit.name || `${termFor('Exhibit')} Lead Image`}
                                            />

                                            <div className="absolute bottom-3 w-full flex justify-center items-center">
                                                {images.map((image, index) => (
                                                    <div
                                                        className={concatenate(index === 0 ? 'bg-white bg-opacity-90 h-3 w-3 border border-1 border-gray-800 box-border' : 'h-2.5 w-2.5 bg-gray-100 bg-opacity-50', 'rounded-full shadow-sm mx-1')}
                                                        key={index}/>
                                                ))}
                                            </div>
                                        </div>
                                        :
                                        <div className="h-full w-full flex items-center justify-center">
                                            <PhotographIcon className="h-20 w-20 text-gray-300"/>
                                        </div>
                                    }
                                </div>

                                {/* Header */}
                                <div className="bg-white px-8 pt-6 pb-2">
                                    <h1 className="font-medium text-2xl">{exhibit.name || 'Exhibit'}</h1>
                                    <p className="mt-3 text-gray-500 text-base font-medium">{exhibitType?.name || termFor('Exhibit Type')}</p>

                                    <div className="mt-4 flex justify-between items-center">
                                        <p className="font-medium text-sm text-gray-500">Your feedback:</p>
                                        <div>
                                            <button
                                                className="rounded-full bg-green-100 border border-2 border-green-500 p-2 mr-5">
                                                <ThumbUpIcon className="h-6 w-6 text-green-700"/>
                                            </button>
                                            <button
                                                className="rounded-full bg-gray-100 border border-2 border-transparent p-2">
                                                <ThumbDownIcon className="h-6 w-6 text-gray-500"/>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Sub Menu */}
                                    <Tab.List className="mt-10 pb-2 grid grid-cols-3 gap-x-6 text-center text-gray-600 font-semibold">
                                            {tabs.map(tab => (
                                                <Tab as={Fragment} key={tab.name}>
                                                    {({selected}) => (
                                                        <button className={
                                                            concatenate(
                                                            selected && 'border-b border-b-4 border-brand text-brand font-medium',
                                                            'cursor-pointer py-1.5')
                                                        }>{tab.name}</button>
                                                    )}
                                                </Tab>
                                            ))}
                                        </Tab.List>
                                </div>

                                {/* Content */}
                                <Tab.Panels>
                                    {tabs.map(tab => (
                                        <Tab.Panel key={tab.name}>{tab.element}</Tab.Panel>
                                    ))}
                                </Tab.Panels>
                            </Tab.Group>
                        </div>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition.Root>
    );
}

export default ExhibitPreview;